import clsx from 'clsx';
import { useRef, useState } from 'react';

import type { CSSProperties, PointerEventHandler, ReactNode } from 'react';

const COLLAPSE_THRESHOLD = 50; // px

export type ResizablePanelProps = {
	position: 'top' | 'right' | 'bottom' | 'left';
	/**
	 * The initial size of the panel. Takes anything you can pass to CSS `width`/`height`.
	 */
	initialSize: string;
	/** Minimum size in pixels the panel can be dragged to. */
	minSize?: number;
	isCollapsed?: boolean;
	onIsCollapsedChange?: (isCollapsed: boolean) => void;
	/** Called when the user finishes a drag-resize with the new size (e.g. `"320px"`); useful for persisting the size. */
	onSizeChange?: (size: string) => void;

	children?: ReactNode;
	className?: string;
	disableContainSize?: boolean;
	id?: string;
	style?: Pick<CSSProperties, 'position' | 'top' | 'right' | 'bottom' | 'left'>;
};

export function ResizablePanel({
	position,
	initialSize,
	minSize,
	isCollapsed = false,
	onIsCollapsedChange,
	onSizeChange,
	children,
	className,
	disableContainSize = false,
	id,
	style,
}: ResizablePanelProps) {
	const orientation = position === 'top' || position === 'bottom' ? 'horizontal' : 'vertical';
	const panelRef = useRef<HTMLDivElement>(null);
	const [panelSize, setPanelSize] = useState<string | null>(null);
	const latestPanelSize = useRef<string | null>(null);
	const dragSurfaceRef = useRef<HTMLDivElement>(null);
	const dragInfo = useRef<DragInfo>({});
	// The panel contents only render once the panel has been opened at least once.
	const [hasBeenOpened, setHasBeenOpened] = useState<boolean>(!isCollapsed);

	const [prevIsCollapsed, setPrevIsCollapsed] = useState<boolean>(isCollapsed);
	if (isCollapsed !== prevIsCollapsed) {
		if (!isCollapsed) {
			// If panel has just been uncollapsed, reset its size to the initial size
			setPanelSize(initialSize);
			setHasBeenOpened(true);
		}
		setPrevIsCollapsed(isCollapsed);
	}

	const handlePointerDown: PointerEventHandler<HTMLDivElement> = (event) => {
		// Prevent text selection on drag
		event.preventDefault();

		dragInfo.current.pointerDownPos = { x: event.clientX, y: event.clientY };

		dragSurfaceRef.current!.setPointerCapture(event.pointerId);
		dragInfo.current.pointerId = event.pointerId;
	};

	const handlePointerMove: PointerEventHandler<HTMLDivElement> = (event) => {
		const pointerDownPos = dragInfo.current.pointerDownPos;
		if (!pointerDownPos) {
			return;
		}

		const pointerMovement =
			position === 'top' ? event.clientY - pointerDownPos.y
			: position === 'right' ? pointerDownPos.x - event.clientX
			: position === 'bottom' ? pointerDownPos.y - event.clientY
			: event.clientX - pointerDownPos.x;

		// Initiate drag if pointer has moved enough
		if (Math.abs(pointerMovement) > 3 && panelRef.current && !dragInfo.current.isDragging) {
			const panelRect = panelRef.current.getBoundingClientRect();
			dragInfo.current.isDragging = {
				initialPanelSize: orientation === 'horizontal' ? panelRect.height : panelRect.width,
			};
		}

		const initialPanelSize = dragInfo.current.isDragging?.initialPanelSize;
		if (initialPanelSize === undefined) {
			return;
		}

		const rawSize = initialPanelSize + pointerMovement;
		const newPanelSize = minSize !== undefined ? Math.max(minSize, rawSize) : rawSize;
		const nextSize = onIsCollapsedChange && newPanelSize < COLLAPSE_THRESHOLD ? '0px' : String(newPanelSize) + 'px';
		latestPanelSize.current = nextSize;
		setPanelSize(nextSize);
	};

	const handlePointerUp: PointerEventHandler<HTMLDivElement> = () => {
		const size = latestPanelSize.current;
		if (onIsCollapsedChange && size === '0px') {
			onIsCollapsedChange(true);
		} else if (dragInfo.current.isDragging && size !== null) {
			onSizeChange?.(size);
		}

		dragInfo.current.pointerDownPos = undefined;
		dragInfo.current.isDragging = undefined;
		if (dragInfo.current.pointerId !== undefined) {
			dragSurfaceRef.current!.releasePointerCapture(dragInfo.current.pointerId);
		}
	};

	if (isCollapsed && !hasBeenOpened) {
		return null;
	}

	return (
		<div
			ref={panelRef}
			style={{
				...style,
				'--panel-size': panelSize ?? initialSize,
				...(minSize !== undefined ?
					orientation === 'horizontal' ?
						{ minHeight: `${minSize}px` }
					:	{ minWidth: `${minSize}px` }
				:	{}),
			}}
			id={id}
			className={clsx(
				'relative grid shrink-0 grow-0 place-items-stretch',
				className,
				orientation === 'horizontal' ? 'h-(--panel-size)' : 'w-(--panel-size)',
				isCollapsed && 'hidden',
			)}
		>
			<div className={clsx('isolate flex min-h-0 min-w-0 flex-col', !disableContainSize && 'contain-[size]')}>
				{children}
			</div>

			<div
				className={clsx(
					'absolute',
					position === 'top' && 'bottom-0 left-0 h-0 w-full cursor-ns-resize',
					position === 'right' && 'top-0 left-0 h-full w-0 cursor-ew-resize',
					position === 'bottom' && 'top-0 left-0 h-0 w-full cursor-ns-resize',
					position === 'left' && 'top-0 right-0 h-full w-0 cursor-ew-resize',
				)}
			>
				<div
					ref={dragSurfaceRef}
					className={clsx(
						'group absolute top-1/2 left-1/2 -translate-1/2 touch-none',
						orientation === 'horizontal' ? 'h-6 w-full cursor-ns-resize' : 'h-full w-2 cursor-ew-resize',
					)}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerUp}
					onPointerCancel={handlePointerUp}
				>
					<div
						className={clsx(
							'absolute top-1/2 left-1/2 -translate-1/2 bg-gray-6 transition-[background-color,height,width] group-hover:bg-blue-9',
							orientation === 'horizontal' ? 'h-px w-full group-hover:h-1' : 'h-full w-px group-hover:w-1',
						)}
					/>
				</div>
			</div>
		</div>
	);
}

interface DragInfo {
	pointerDownPos?: { x: number; y: number };
	pointerId?: number;
	isDragging?: {
		initialPanelSize: number;
	};
}
